import {
  Assignment,
  ClaimAssigneeRelationshipCanon,
  ContactAssignee,
  JobAssigneeRelationship,
} from '@eigtech/assignments-types'
import { Contact } from '@eigtech/contacts-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { useGetJob } from '@/api'
import { JobNoteFormDrawer, JobTimeline } from '@/components/Domains/Jobs'
import { ROUTER_PATHS } from '@/lib/constants'
import { Can } from '@/lib/permissions'
import { forceDateInputValueToTimezone } from '@/lib/utils'
import { HTTPError } from '@eigtech/ui-shared-api'
import { useGetAssignmentsForAssignee } from '@eigtech/ui-shared-assignments'
import { getContactLicenseNumber, getContactName } from '@eigtech/ui-shared-contacts'
import { toZonedTime, tzFormat } from '@eigtech/ui-shared-dates'
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  ComposedAlert,
  ComposedCard,
  ComposedCardProps,
  Flex,
  HStack,
  Property,
  PropertyList,
  Skeleton,
  Stack,
  Tag,
  Text,
  Wrap,
  WrapItem,
  forwardRef,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import { getJobLabelFromId, getJobServiceLabel, getJobTypeLabel } from '@eigtech/ui-shared-jobs'
import {
  CustomerContactAttemptedDrawer,
  JobWorkDetailsActionMenu,
  JobWorkDetailsCard,
} from '@eigtech/ui-shared-jobs-web'
import { LinkButton } from '@eigtech/ui-shared-router'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

export const Route = createFileRoute('/_auth/jobs/$jobId/_layout/info')({
  component: JobDetailInfo,
})

function JobDetailInfo() {
  const { jobId } = Route.useParams()

  const jobNumber = getJobLabelFromId(jobId)

  const { data: job, isPending, isError, error } = useGetJob(jobId)

  const hasJobWorkAuth = !!job?.workAuths?.length
  const signedJobWorkAuth = job?.workAuths?.find((w) => !!w.signedOn)

  if (isError) {
    if (error instanceof HTTPError) {
      if (error.response.status === 404 || error.response.status === 403) {
        return (
          <ComposedAlert
            alert={{
              title: 'The requested job does not exist',
              description:
                'If you believe this to be an error, please contact EIG Customer Service.',
            }}
            status="error"
          />
        )
      }
    }

    return (
      <ComposedAlert
        alert={{
          title: 'Could not retrieve job information.',
          description:
            'Please try again by refreshing the page. If the issue persists, pleae contact EIG Customer Service.',
        }}
        status="error"
      />
    )
  }

  return (
    <Stack>
      {hasJobWorkAuth ? (
        signedJobWorkAuth ? (
          <ComposedAlert
            alert={{
              description: (
                <Box>
                  <Text>Work Authorization signed - this job is ready to be worked.</Text>
                </Box>
              ),
            }}
            status="success"
          />
        ) : (
          <ComposedAlert
            alert={{
              description: (
                <Box>
                  <Text>
                    Work Authorization for this job has not been signed and requires immediate
                    attention.
                  </Text>
                </Box>
              ),
            }}
            status="error"
          />
        )
      ) : (
        <ComposedAlert
          alert={{
            description: (
              <Box>
                <Text>
                  A Work Authorization has not been created for this job yet. Once it has been
                  created it will need to be signed before work can begin. Please contact EIG
                  Customer Service with any questions.
                </Text>
              </Box>
            ),
          }}
          status="warning"
        />
      )}
      {job?.isClosed && (
        <ComposedAlert
          alert={{
            title: 'Job has been closed',
            description: (
              <Box>
                <Text>Reason: {job.closedReason ?? 'Unknown'}</Text>
                <Text>If you believe this is an error, please contact EIG Customer Service.</Text>
              </Box>
            ),
          }}
          status="warning"
        />
      )}

      <Stack direction={{ base: 'column', lg: 'row' }} spacing={6}>
        <Stack flexGrow={1} spacing="6">
          <ComposedCard heading="Job Details" w="full">
            <PropertyList
              labelWidth="48"
              p="0"
              variant={{ base: 'vertical-stacked', sm: 'vertical' }}
            >
              <Property label="Job Number" value={jobNumber} />
              <Property
                label="Type"
                value={
                  <Skeleton isLoaded={!isPending}>
                    {job ? (
                      <Tag colorScheme="blue">{getJobTypeLabel(job.jobType)}</Tag>
                    ) : (
                      'Loading...'
                    )}
                  </Skeleton>
                }
              />
              <Property
                label="Services"
                value={
                  <Skeleton isLoaded={!isPending}>
                    {job ? (
                      <Wrap spacing="1">
                        {job.services.map((service) => (
                          <WrapItem key={service}>
                            <Tag colorScheme="teal">{getJobServiceLabel(service)}</Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      'Loading...'
                    )}
                  </Skeleton>
                }
              />
              <Property
                label="Description"
                value={
                  <Skeleton isLoaded={!isPending}>{job?.jobDescription ?? 'Loading...'}</Skeleton>
                }
                whiteSpace="pre-line"
              />
            </PropertyList>
          </ComposedCard>

          <ComposedCard heading="Job Location">
            <PropertyList
              labelWidth="48"
              p="0"
              variant={{ base: 'vertical-stacked', sm: 'vertical' }}
            >
              <Property label="Street" value={job?.jobLocation.address.addressLine1} />
              {!!job?.jobLocation.address.addressLine2 && (
                <Property label="Secondary Street" value={job.jobLocation.address.addressLine2} />
              )}
              {!!job?.jobLocation.address.addressLine3 && (
                <Property label="Secondary Street" value={job.jobLocation.address.addressLine3} />
              )}
              {!!job?.jobLocation.address.addressType && (
                <Property label="Address Type" value={job.jobLocation.address.addressType} />
              )}
              <Property label="City" value={job?.jobLocation.address.city} />
              <Property label="County" value={job?.jobLocation.address.countyOrParishCode} />
              <Property label="State" value={job?.jobLocation.address.stateOrProvince} />
              <Property label="ZIP Code" value={job?.jobLocation.address.zipOrPostalCode} />
            </PropertyList>
          </ComposedCard>

          <JobWorkDetailsCard isPendingJob={isPending} job={job} />

          <JobDetailInfoTeam />
        </Stack>

        <Stack spacing="6" w={{ base: 'full', lg: '96', xl: '28rem' }}>
          <JobDetailInfoActions />

          <ComposedCard heading="Job Visualizer" w="full">
            <JobTimeline
              jobId={jobId}
              maxH={{ base: '75vh', md: '50vh' }}
              overflowY="auto"
              pl="1"
              py="2"
            />
          </ComposedCard>
        </Stack>
      </Stack>
    </Stack>
  )
}

function JobDetailInfoActions() {
  const { jobId } = Route.useParams()

  const { data: job, isPending } = useGetJob(jobId)

  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false)
  const {
    isOpen: isCustomerCommunicationOpen,
    onClose: onCloseCustomerCommunication,
    onOpen: onOpenCustomerCommunication,
  } = useDisclosure()

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timezoneAbbreviation = tzFormat(toZonedTime(new Date(), timezone), 'zzz')

  return (
    <>
      <ComposedCard heading="Actions" w="full">
        <ButtonGroup orientation="vertical" spacing="4" variant="outline" w="full">
          <JobWorkDetailsActionMenu isPendingJob={isPending} job={job} />

          <Can I="create" a="note">
            <Button onClick={() => setIsAddNoteOpen(true)}>Add a Note</Button>
          </Can>

          <Button onClick={onOpenCustomerCommunication}>Record Customer Communication</Button>

          <Can I="read" a="document">
            <ButtonGroup isAttached variant="outline">
              <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailDocuments.route} w="full">
                View Documents
              </LinkButton>
              <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailMedia.route} w="full">
                View Media
              </LinkButton>
            </ButtonGroup>
          </Can>

          <Can I="create" a="document">
            <ButtonGroup isAttached variant="outline">
              <LinkButton
                params={{ jobId }}
                to={ROUTER_PATHS.jobDetailUploadDocuments.route}
                w="full"
              >
                Upload Documents/Media
              </LinkButton>
            </ButtonGroup>

            <ButtonGroup isAttached variant="outline">
              <LinkButton params={{ jobId }} to={ROUTER_PATHS.jobDetailPhotoReport.route} w="full">
                Create Photo Report
              </LinkButton>
            </ButtonGroup>
          </Can>
        </ButtonGroup>
      </ComposedCard>

      {isAddNoteOpen && (
        <JobNoteFormDrawer jobId={jobId} open={isAddNoteOpen} onOpenChange={setIsAddNoteOpen} />
      )}
      {isCustomerCommunicationOpen && (
        <CustomerContactAttemptedDrawer
          formToPayload={({ contactedBy, contactAttemptedDate, customerContacted, ...data }) => ({
            contactedBy: contactedBy.contactId,
            contactAttemptedDate: forceDateInputValueToTimezone(
              new Date(contactAttemptedDate),
              timezone
            ).toISOString(),
            customerContacted: customerContacted.contactId,
            jobId,
            ...data,
          })}
          isOpen
          jobId={jobId}
          timezoneAbbreviation={timezoneAbbreviation}
          onClose={onCloseCustomerCommunication}
        />
      )}
    </>
  )
}

function JobDetailInfoTeam() {
  const { jobId } = Route.useParams()

  const { data: assignments } = useGetAssignmentsForAssignee(ensureJobCorn(jobId))

  const approver = getContactFromAssignment('approver', assignments)
  const coordinator = getContactFromAssignment('coordinator', assignments)
  const dispatcher = getContactFromAssignment('dispatcher', assignments)
  const primaryContact = getContactFromAssignment('primaryContact', assignments)
  const projectManager = getContactFromAssignment('projectManager', assignments)
  const fieldTechnician = getContactFromAssignment('fieldTechnician', assignments)
  const subcontractor = getContactFromAssignment('subcontractor', assignments)

  return (
    <ComposedCard heading="Your Job Team" w="full">
      <Flex alignItems="stretch" flexWrap="wrap" gap="2">
        <Can I="read" a="primaryContact">
          <TeamMember contact={primaryContact} label="Primary Contact" />
        </Can>
        <Can I="read" a="coordinator">
          <TeamMember contact={coordinator} label="Coordinator" />
        </Can>
        <Can I="read" a="projectManager">
          <TeamMember contact={projectManager} label="Project Manager" />
        </Can>
        <Can I="read" a="approver">
          <TeamMember contact={approver} label="Approver" />
        </Can>
        <Can I="read" a="dispatcher">
          <TeamMember contact={dispatcher} label="Dispatcher" />
        </Can>
        <Can I="read" a="fieldTechnician">
          <TeamMember contact={fieldTechnician} label="Field Technician" />
        </Can>
        <Can I="read" a="subcontractor">
          <TeamMember contact={subcontractor} label="Subcontractor" />
        </Can>
      </Flex>
    </ComposedCard>
  )
}

type TeamMemberProps = { contact: Contact | undefined; label: string } & ComposedCardProps

const TeamMember = forwardRef<TeamMemberProps, 'div'>(function TeamMember(
  { contact, label, ...props },
  ref
) {
  const licenseNumber = getContactLicenseNumber(contact)

  return (
    <ComposedCard
      ref={ref}
      boxShadow="none"
      cardBodyProps={{ px: { base: '4', md: '6' } }}
      variant="outline"
      w={{ base: 'full', md: 'auto' }}
      {...props}
    >
      <HStack spacing="4">
        <Avatar size={{ base: 'lg', md: 'xl' }} src={contact?.picture ?? undefined} />
        <Stack spacing="1">
          <Text
            aria-label={`${label}'s name`}
            as="span"
            fontSize="lg"
            fontWeight="bold"
            lineHeight={1.2}
          >
            {getContactName(contact, 'Unassigned')}
          </Text>
          <Stack spacing="0.5">
            <Text aria-label="Position" as="span" lineHeight={1.2}>
              {label}
            </Text>
            {licenseNumber && <Text as="span" lineHeight={1.2}>{`License #${licenseNumber}`}</Text>}
          </Stack>
        </Stack>
      </HStack>
    </ComposedCard>
  )
})

const getContactFromAssignment = (
  relationship: JobAssigneeRelationship | ClaimAssigneeRelationshipCanon,
  assignments: Assignment[] | undefined
) =>
  (
    assignments?.find(
      (assignment) =>
        assignment.assignee.type === 'contact' &&
        assignment.assignable.assigneeRelationship === relationship
    )?.assignee as ContactAssignee | undefined
  )?.contact
